import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { decryptText, decryptAppId } from "../../utils/encryption";
import axios from "axios";
import jsPDF from "jspdf";

const AssessorLogisticsAgreement = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ─── Get encrypted params from URL ──────────────────────────
  const getOrgIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get("orgId");
    if (encryptedOrgId) {
      try {
        return decryptText(encryptedOrgId);
      } catch {
        return encryptedOrgId;
      }
    }
    return null;
  };

  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        return decryptAppId(encryptedAppId);
      } catch {
        return encryptedAppId;
      }
    }
    return null;
  };

  // ─── State ──────────────────────────────────────────────────
  const [orgId] = useState(getOrgIdFromQuery());
  const [appId] = useState(getAppIdFromQuery());
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    chiefExecutiveName: "",
    designation: "",
  });
  const [errors, setErrors] = useState({});

  // ─── Validation ─────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    if (!formData.chiefExecutiveName.trim()) {
      newErrors.chiefExecutiveName =
        "Name of Head of the Laboratory is required";
    }
    if (!formData.designation.trim()) {
      newErrors.designation = "Designation is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // ─── PDF Generation (with NABL binding and fixed table) ──
  const generatePDFBlob = (nablOfficialName, nablReceiptDate) => {
    return new Promise((resolve, reject) => {
      try {
        const doc = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
          compress: true,
        });

        const pageWidth = 210;
        const pageHeight = 297;
        const margin = 20;
        const contentWidth = pageWidth - 2 * margin;
        let y = margin;
        let pageNumber = 1;
        const font = "times";

        const addNewPage = () => {
          addFooter();
          doc.addPage();
          y = margin;
          pageNumber++;
        };

        const checkPageOverflow = (requiredSpace = 10) => {
          if (y + requiredSpace > pageHeight - 20) {
            addNewPage();
          }
        };

        const addSectionHeading = (text) => {
          checkPageOverflow(10);
          y += 3;
          doc.setFont(font, "bold");
          doc.setFontSize(12);
          doc.text(text, margin, y);
          y += 6;
          doc.setFont(font, "normal");
          doc.setFontSize(10);
        };

        const addNumberedParagraph = (number, text) => {
          const numWidth = 15;
          doc.setFont(font, "bold");
          doc.setFontSize(10);
          doc.text(number, margin, y);

          doc.setFont(font, "normal");
          const textLines = doc.splitTextToSize(text, contentWidth - numWidth);
          const requiredHeight = textLines.length * 5.5;
          checkPageOverflow(requiredHeight);
          doc.text(textLines, margin + numWidth, y);
          y += textLines.length * 5.5 + 1.5;
        };

        const addIndentedParagraph = (text, indent) => {
          doc.setFont(font, "normal");
          doc.setFontSize(10);
          const lines = doc.splitTextToSize(text, contentWidth - indent);
          const requiredHeight = lines.length * 5.5;
          checkPageOverflow(requiredHeight);
          doc.text(lines, margin + indent, y);
          y += lines.length * 5.5 + 1.5;
        };

        const addFooter = () => {
          doc.setFontSize(8);
          doc.setFont(font, "normal");
          doc.text(`Page ${pageNumber}`, pageWidth / 2, pageHeight - 10, {
            align: "center",
          });
        };

        // ─── Title ─────────────────────────────────────────────
        doc.setFont(font, "bold");
        doc.setFontSize(18);
        doc.text("ASSESSOR LOGISTICS AGREEMENT", pageWidth / 2, y, {
          align: "center",
        });
        y += 12;

        // ─── Introduction ─────────────────────────────────────
        doc.setFont(font, "normal");
        doc.setFontSize(10);
        let introText =
          "The terms and conditions mentioned in this document are considered as assessor logistics agreement between QCI and Conformity Assessment Body (CAB). By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all the Terms and Conditions mentioned in the agreement.";
        let introLines = doc.splitTextToSize(introText, contentWidth);
        doc.text(introLines, margin, y);
        y += introLines.length * 5.5 + 2;

        introText =
          'QCI and the CAB are hereinafter individually referred to as a "Party" and collectively as the "Parties."';
        introLines = doc.splitTextToSize(introText, contentWidth);
        doc.text(introLines, margin, y);
        y += introLines.length * 5.5 + 6;

        // ─── Section 1 ─────────────────────────────────────────
        addSectionHeading("1. Scope and Nature of Services");
        addNumberedParagraph(
          "1.1.",
          "The parties acknowledges that QCI acts solely as a facilitator and coordinator of logistics for Assessors/Technical Experts deployed for the CAB's assessment(s) and does not function as a travel agency, transport operator, hotel operator, insurer or guarantor.",
        );
        addNumberedParagraph(
          "1.2.",
          "Nothing contained in this agreement shall be construed as creating any contractual relationship between QCI and any Third-Party Service Provider on behalf of the CAB.",
        );
        addNumberedParagraph(
          "1.3.",
          "QCI shall exercise reasonable care while facilitating logistics arrangements. However, QCI shall not be liable for delays, cancellations, overbooking, denial of boarding, baggage loss, force majeure events, service deficiencies or any other acts or omissions attributable to Third-Party Service Providers.",
        );

        // ─── Section 2 ─────────────────────────────────────────
        addSectionHeading("2. Responsibilities of QCI:");
        addNumberedParagraph(
          "2.1.",
          "QCI shall facilitate travel (air/rail/road) and accommodation for Assessors/Technical Experts conducting CAB assessment activities as per eligibility and travel preference as detailed in Annexure I as amended from time to time.",
        );
        addNumberedParagraph(
          "2.2.",
          "QCI shall endeavour to arrange travel (flights/train/bus) so that Assessors/Technical Experts arrive in the assessment city a day prior to commencement of the assessment, preferably in the afternoon/evening.",
        );
        addNumberedParagraph(
          "2.3.",
          "QCI shall endeavour to arrange return travel on the assessment day, allowing sufficient time to reach the airport after completion of assessment duration. If suitable flights are unavailable or the location is remote, return travel may be arranged for the next day.",
        );
        addNumberedParagraph(
          "2.4.",
          "QCI shall book refundable air tickets in Economy Class or, for rail travel, 2nd Class AC tickets or such class as may be prescribed by QCI from time to time.",
        );
        addNumberedParagraph(
          "2.5.",
          "QCI shall book the tickets, which will be including meals for the Assessor/Technical Expert.",
        );
        addNumberedParagraph(
          "2.6.",
          "QCI shall book the accommodation/hotel for Assessor/Technical Expert as per the eligibility criteria approved by QCI. The currently approved criteria are available at Annexure.",
        );
        addNumberedParagraph(
          "2.7.",
          "QCI shall, to the extent possible, make the stay arrangement near to the assessment venue.",
        );
        addNumberedParagraph(
          "2.8.",
          "QCI shall make Hotel arrangements including meals (Breakfast & Dinner). In case due to limited flight, the Assessor arrives early, lunch also will be provided and will be charged to the CAB.",
        );
        addNumberedParagraph(
          "2.9.",
          "QCI shall invoice to the CAB for the expenses towards Travel/lodging & Boarding on actual basis.",
        );
        addNumberedParagraph(
          "2.10.",
          "QCI shall share the tickets/hotel booking to the CAB for coordination.",
        );

        // ─── Section 3 ─────────────────────────────────────────
        addSectionHeading("3. Responsibilities of the CAB:");
        addNumberedParagraph(
          "3.1.",
          "CAB shall arrange the local travel for Assessor/Technical Expert in the city where assessment is scheduled including to & from Airport pickup/drop & hotel to assessment venue.",
        );
        addNumberedParagraph(
          "3.2.",
          "In case where local Assessor/Technical Expert is deputed, the local transportation/taxi for the Assessor/Technical Expert will be arranged by CAB.",
        );
        addNumberedParagraph(
          "3.3.",
          "CAB will make the arrangements for working lunch during the Assessment days.",
        );
        addNumberedParagraph(
          "3.4.",
          "CAB shall reimburse QCI for invoiced expenses within 30 days from the invoice date. A late fee of 1% per month shall apply for the payments delayed beyond 30 days.",
        );
        addNumberedParagraph(
          "3.5.",
          "CAB shall bear the cancellation or modification charges levied by Airlines, hotel or service providers except in the following cases:",
        );
        addIndentedParagraph(
          "a. If QCI request changes or cancellations after bookings have been confirmed.",
          25,
        );
        addIndentedParagraph(
          "b. If the assessor's schedule is altered due to last-minute requests or internal changes by QCI.",
          25,
        );
        addNumberedParagraph(
          "3.6.",
          "CAB shall adhere to the timeline for payment and shall not withhold, deduct or set off payments against any claims, dispute or adjustment unless agreed in writing by QCI.",
        );
        addNumberedParagraph(
          "3.7.",
          "CAB shall not attempt to influence, directly or indirectly, the Assessor(s) in discharge of their professional duties.",
        );
        addNumberedParagraph(
          "3.8.",
          "All arrangements made by CAB shall be reasonable, non-influencing and compliant with QCI policies.",
        );
        addNumberedParagraph(
          "3.9.",
          "Any attempt to provide inducement, hospitality beyond norms, or influence the assessment process shall constitute serious non-compliance and material breach to this agreement.",
        );

        // ─── Section 4 ─────────────────────────────────────────
        addSectionHeading("4. Data Protection and Privacy (DPDP Act 2023)");
        addNumberedParagraph(
          "4.1.",
          "The Parties agree that any personal data processed under this Agreement shall be limited strictly to what is necessary for facilitating assessor logistics and related assessment activities.",
        );
        addNumberedParagraph(
          "4.2.",
          "The CAB shall process such data strictly in accordance with the scope communicated by QCI ensuring the compliance of DPDP Act and Rules.",
        );
        addNumberedParagraph(
          "4.3.",
          "The CAB shall implement reasonable security safeguards to prevent unauthorized access, disclosure, alteration, or loss of personal data, in compliance with the Digital Personal Data Protection Act, 2023. CAB shall promptly notify the QCI upon becoming aware of any personal data breach affecting information processed under this Agreement and shall reasonably cooperate in mitigating its consequences.",
        );
        addNumberedParagraph(
          "4.4.",
          "QCI shall implement reasonable security safeguards in compliance with Digital Personal Data Protection Act, 2023, and applicable rules, strictly for the specified purpose.",
        );
        addNumberedParagraph(
          "4.5.",
          "The CAB represents/warrants/confirms that it has the lawful authority to share any personal data provided to QCI for the purposes of this Agreement.",
        );
        addNumberedParagraph(
          "4.6.",
          "Personal data may be shared only with third-party service providers such as airlines, hotels, and transport agencies, strictly to the extent required for fulfilment of the specified purpose.",
        );
        addNumberedParagraph(
          "4.7.",
          "Personal data shall not be retained beyond the period necessary for completion of the assessment and settlement of related obligations, unless required by law.",
        );
        addNumberedParagraph(
          "4.8.",
          "Both Parties shall maintain confidentiality of information exchanged under this Agreement, except where disclosure is required under law or regulatory directions.",
        );
        addNumberedParagraph(
          "4.9.",
          "Nothing in this Agreement shall be construed as creating obligations exceeding those mandated under applicable data protection law.",
        );

        // ─── Section 5 ─────────────────────────────────────────
        addSectionHeading("5. Confidentiality");
        let confText =
          "Both Parties shall treat all information exchanged under this Agreement as confidential and not disclose it to third parties without prior written consent, except as required by law.";
        let confLines = doc.splitTextToSize(confText, contentWidth);
        doc.setFont(font, "normal");
        doc.setFontSize(10);
        checkPageOverflow(confLines.length * 5.5);
        doc.text(confLines, margin, y);
        y += confLines.length * 5.5 + 2;

        confText =
          "CAB shall keep confidential all information received from QCI or its personnel in the course of the assessment and shall not disclose it to any third party except without prior written consent or as required by law.";
        confLines = doc.splitTextToSize(confText, contentWidth);
        checkPageOverflow(confLines.length * 5.5);
        doc.text(confLines, margin, y);
        y += confLines.length * 5.5 + 2;

        // ─── Section 6 ─────────────────────────────────────────
        addSectionHeading("6. Indemnity");
        let indemnityText =
          "The CAB shall indemnify, defend and hold harmless QCI, its Boards, employees, Assessors and Technical Experts from and against all claims, losses, damages, liabilities, costs and expenses including legal fees arising out of or in connection with (a) acts or omissions of the CAB or its personnel; (b) breach of this Agreement by the CAB; or (c) injury, death, loss or damage sustained by any Personnel while performing Assessment Activities due to conditions attributable to the CAB. The CAB shall indemnify QCI against claims arising from logistics mismanagement or DPDP non-compliance attributable to the CAB.";
        let indemnityLines = doc.splitTextToSize(indemnityText, contentWidth);
        checkPageOverflow(indemnityLines.length * 5.5);
        doc.text(indemnityLines, margin, y);
        y += indemnityLines.length * 5.5 + 2;

        // ─── Section 7 ─────────────────────────────────────────
        addSectionHeading("7. Limitation of Liability");
        addNumberedParagraph(
          "7.1.",
          "Except for liability arising from fraud, wilful misconduct or obligations expressly assumed under this Agreement, QCI shall not be liable for any indirect, consequential, incidental, special or exemplary damages, including loss of business, revenue, profits, goodwill or opportunity.",
        );
        addNumberedParagraph(
          "7.2.",
          "QCI shall not be liable for any delay, cancellation, rescheduling or service deficiency attributable to Third-Party Service Providers or events beyond its reasonable control.",
        );

        // ─── Section 8 ─────────────────────────────────────────
        addSectionHeading("8. Force Majeure:");
        let forceText =
          "Neither party shall be liable for service deficiencies, delays, or disruptions caused by airlines, hotels, railways, or other travel vendors, including those due to force majeure events such as natural disasters, strikes, epidemics, pandemics, war, civil unrest, strikes, governmental restrictions, failure of transportation systems or other unforeseen circumstances beyond parties reasonable control.";
        let forceLines = doc.splitTextToSize(forceText, contentWidth);
        checkPageOverflow(forceLines.length * 5.5);
        doc.text(forceLines, margin, y);
        y += forceLines.length * 5.5 + 2;

        // ─── Section 9 ─────────────────────────────────────────
        addSectionHeading("9. Term and Validity:");
        let termText =
          "Unless either Party gives the other Party at least 30 days' prior notice of its intention not to renew, this Agreement shall continue on assessment basis, for every new planned assessment, on the same terms and conditions.";
        let termLines = doc.splitTextToSize(termText, contentWidth);
        checkPageOverflow(termLines.length * 5.5);
        doc.text(termLines, margin, y);
        y += termLines.length * 5.5 + 2;

        termText =
          "Notwithstanding the expiry of the Term, where any assessment activity or travel/hotel bookings have already been scheduled or committed by QCI before expiry, the provisions of this Agreement including payment obligations shall continue to apply until completion of such activity and settlement of all dues.";
        termLines = doc.splitTextToSize(termText, contentWidth);
        checkPageOverflow(termLines.length * 5.5);
        doc.text(termLines, margin, y);
        y += termLines.length * 5.5 + 2;

        // ─── Section 10 ────────────────────────────────────────
        addSectionHeading("10. Termination:");
        let termText1 =
          "Either Party may terminate this Agreement by giving 30 (thirty) days' prior written notice to the other Party. Termination shall not affect rights and obligations accrued prior to termination. In the event of such termination:";
        let termLines1 = doc.splitTextToSize(termText1, contentWidth);
        checkPageOverflow(termLines1.length * 5.5);
        doc.text(termLines1, margin, y);
        y += termLines1.length * 5.5 + 2;

        addIndentedParagraph(
          "(a) QCI shall be entitled to receive full payment for:",
          15,
        );
        addIndentedParagraph(
          "(i) all amounts invoiced and unpaid as of the termination date; and",
          30,
        );
        addIndentedParagraph(
          "(ii) all non-cancellable or committed expenses incurred on behalf of the CAB prior to termination.",
          30,
        );
        addIndentedParagraph(
          "(b) Any bookings already made for Assessors/Technical Experts may either be utilized by the CAB or cancelled at the CAB's cost including cancellation/modification charges.",
          15,
        );

        termText1 =
          "QCI may terminate this Agreement immediately by written notice if the QCI:";
        termLines1 = doc.splitTextToSize(termText1, contentWidth);
        checkPageOverflow(termLines1.length * 5.5);
        doc.text(termLines1, margin, y);
        y += termLines1.length * 5.5 + 2;

        addIndentedParagraph("(i) fails to make timely payments,", 30);
        addIndentedParagraph(
          "(ii) breaches any material provision of this Agreement, or",
          30,
        );
        addIndentedParagraph(
          "(iii) engages in conduct that, in QCI's reasonable opinion, adversely affects QCI's reputation or the integrity of the assessment process.",
          30,
        );

        termText1 =
          "Upon such termination, all amounts due to QCI shall become immediately payable.";
        termLines1 = doc.splitTextToSize(termText1, contentWidth);
        checkPageOverflow(termLines1.length * 5.5);
        doc.text(termLines1, margin, y);
        y += termLines1.length * 5.5 + 2;

        // ─── Section 11 ────────────────────────────────────────
        addSectionHeading("11. Dispute Resolution:");
        let disputeText =
          "In the event of any dispute arising out of or in connection with this Agreement, the parties shall try to resolve the dispute amicably by mutual discussion. The unresolved disputes shall be referred to the Sole Arbitrator to be appointed by the mutual consent of the parties and the arbitration proceedings shall be conducted in accordance with the Arbitration and Conciliation Act, 1996 as amended upto date. In the event the Parties are unable to reach mutual consensus on the appointment of the Sole Arbitrator within thirty (30) days from the date of invocation of arbitration, the Sole Arbitrator shall be appointed in accordance with the provisions of Section 11 of the Arbitration and Conciliation Act, 1996, by the competent court. The venue/seat of the arbitration proceedings shall be at New Delhi. The arbitration proceedings shall be conducted in English language. The decision rendered by the Sole Arbitrator shall be final and binding on both Parties. Nothing contained herein shall prevent either Party from approaching the courts at New Delhi for interim reliefs, including injunctions, pending completion of the arbitration proceedings.";
        let disputeLines = doc.splitTextToSize(disputeText, contentWidth);
        checkPageOverflow(disputeLines.length * 5.5);
        doc.text(disputeLines, margin, y);
        y += disputeLines.length * 5.5 + 2;

        // ─── Section 12 ────────────────────────────────────────
        addSectionHeading("12. Governing Law and Territorial Jurisdiction:");
        let govText =
          "This Agreement shall be governed by and construed in accordance with the laws of India. The parties hereby agree that the courts at New Delhi shall have exclusive territorial jurisdiction over any dispute arising out of or in connection with this Agreement.";
        let govLines = doc.splitTextToSize(govText, contentWidth);
        checkPageOverflow(govLines.length * 5.5);
        doc.text(govLines, margin, y);
        y += govLines.length * 5.5 + 2;

        // ─── Section 13 ────────────────────────────────────────
        addSectionHeading("13. General Provisions");
        addNumberedParagraph(
          "13.1.",
          "Nothing contained herein shall create any partnership, agency, employment or joint venture between the Parties.",
        );
        addNumberedParagraph(
          "13.2.",
          "Any amendment shall be valid only if made in writing and signed by both Parties.",
        );
        addNumberedParagraph(
          "13.3.",
          "If any provision is held invalid or unenforceable, the remaining provisions shall continue in full force and effect.",
        );
        addNumberedParagraph(
          "13.4.",
          "Failure by either Party to enforce any provision shall not constitute a waiver of its rights.",
        );
        addNumberedParagraph(
          "13.5.",
          "Neither Party shall assign its rights or obligations under this Agreement without the prior written consent of the other Party.",
        );
        addNumberedParagraph(
          "13.6.",
          "Notices under this Agreement shall be in writing and delivered by registered post, courier or electronic mail to the addresses notified by the Parties.",
        );
        addNumberedParagraph(
          "13.7.",
          "The provisions relating to payment obligations, confidentiality, indemnity, limitation of liability, data protection, dispute resolution and any other provision intended by its nature to survive shall continue notwithstanding expiry or termination of this Agreement.",
        );

        // ─── Section 14 ────────────────────────────────────────
        addSectionHeading("14. Entire Agreement & Amendments");
        let entText =
          "This Agreement constitutes the entire understanding between the Parties. Any amendments shall be in writing and signed by authorized representatives of the Parties.";
        let entLines = doc.splitTextToSize(entText, contentWidth);
        checkPageOverflow(entLines.length * 5.5);
        doc.text(entLines, margin, y);
        y += entLines.length * 5.5 + 5;

        // ─── Witness statement ─────────────────────────────────
        checkPageOverflow(20);
        y += 5;
        doc.setFont(font, "bold");
        const witnessText =
          "IN WITNESS WHEREOF, the Parties hereto have executed this Agreement as of the day and year written herein above.";
        const witnessLines = doc.splitTextToSize(witnessText, contentWidth);
        doc.text(witnessLines, margin, y);
        y += witnessLines.length * 5.5 + 10;

        // ─── ANNEXURE 1 ────────────────────────────────────────
        doc.setFont(font, "bold");
        doc.setFontSize(14);
        doc.text("ANNEXURE 1:", pageWidth / 2, y, { align: "center" });
        y += 10;

        doc.setFont(font, "bold");
        doc.setFontSize(12);
        doc.text("Air Travel Criteria", margin, y);
        y += 8;

        doc.setFont(font, "bold");
        doc.setFontSize(10);
        doc.text("Domestic Travel", margin, y);
        y += 6;

        doc.setFont(font, "normal");
        doc.setFontSize(9);
        const bulletPoints = [
          "Air tickets shall be booked based on the best available rates at the time of booking.",
          "Class & Seat Preference: Economy Class only.",
          "Seat Preference: Seat will be booked in the order: Aisle > Window",
          "Meal Entitlement: Standard airline meal included in fare.",
        ];
        bulletPoints.forEach((point) => {
          checkPageOverflow(6);
          doc.text("• " + point, margin + 5, y);
          y += 5.5;
        });
        y += 4;

        doc.setFont(font, "bold");
        doc.setFontSize(10);
        doc.text("Train:", margin, y);
        y += 6;
        doc.setFont(font, "normal");
        doc.setFontSize(9);
        doc.text("• 2nd AC", margin + 5, y);
        y += 8;

        doc.setFont(font, "bold");
        doc.setFontSize(10);
        doc.text("Hotel Eligibility Criteria", margin, y);
        y += 8;

        // ─── IMPROVED TABLE (fits in margins, wraps text) ───
        const tableData = [
          [
            "A-Class Cities",
            "Mumbai, Delhi, Kolkata, Chennai, Bengaluru, Hyderabad",
            "Rs. 10,000 + Taxes as applicable",
          ],
          [
            "B-Class Cities",
            "State Capitals and Other Identified Towns (per QCI's need)",
            "Rs. 8,000 + Taxes as applicable",
          ],
          [
            "C-Class Cities",
            "All other cities",
            "Rs. 6,000 + Taxes as applicable",
          ],
        ];

        const headers = [
          "City Category",
          "Cities",
          "Hotel Rate (per night) (Single Occupancy Rooms)",
        ];

        // Column widths sum to 170mm (content width = 170)
        const colWidths = [30, 85, 55];
        const tableX = margin;
        let tableY = y;

        // --- Header row (wrapped) ---
        const headerLinesArray = headers.map((h, i) => {
          return doc.splitTextToSize(h, colWidths[i] - 4);
        });
        let headerMaxLines = 1;
        headerLinesArray.forEach((lines) => {
          if (lines.length > headerMaxLines) headerMaxLines = lines.length;
        });
        const headerHeight = headerMaxLines * 5 + 4;

        doc.setFont(font, "bold");
        doc.setFontSize(9);
        headers.forEach((_, i) => {
          const x = tableX + colWidths.slice(0, i).reduce((a, b) => a + b, 0);
          doc.rect(x, tableY, colWidths[i], headerHeight, "S");
          const lines = headerLinesArray[i];
          doc.text(lines, x + 2, tableY + 4);
        });
        tableY += headerHeight;

        // --- Data rows (dynamic height) ---
        doc.setFont(font, "normal");
        tableData.forEach((row) => {
          const rowLines = row.map((cell, i) => {
            return doc.splitTextToSize(cell, colWidths[i] - 4);
          });
          let maxLines = 1;
          rowLines.forEach((lines) => {
            if (lines.length > maxLines) maxLines = lines.length;
          });
          const rowHeight = maxLines * 5 + 4;

          row.forEach((_, i) => {
            const x = tableX + colWidths.slice(0, i).reduce((a, b) => a + b, 0);
            doc.rect(x, tableY, colWidths[i], rowHeight, "S");
            const lines = rowLines[i];
            doc.text(lines, x + 2, tableY + 4);
          });
          tableY += rowHeight;
        });

        y = tableY + 5;

        // Note
        doc.setFont(font, "italic");
        doc.setFontSize(8);
        const noteText =
          "Note: For QCI/NABL officials acting as Assessors/Technical Experts, travel and accommodation arrangements shall be made as per their eligibility under QCI Service Rules.";
        const noteLines = doc.splitTextToSize(noteText, contentWidth);
        checkPageOverflow(noteLines.length * 5);
        doc.text(noteLines, margin, y);
        y += noteLines.length * 5 + 8;

        // ─── Signature Block ───────────────────────────────────
        const currentDate = new Date();
        const dateStr = currentDate.toLocaleString("en-US", {
          month: "numeric",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
          hour12: true,
        });

        // Authorised Representative of CAB
        doc.setFont(font, "bold");
        doc.setFontSize(11);
        doc.text("Authorised Representative of CAB", margin, y);
        y += 8;
        doc.setFont(font, "normal");
        doc.setFontSize(10);
        doc.text(
          `Name of Head of the Laboratory: ${formData.chiefExecutiveName || "___________________"}`,
          margin,
          y,
        );
        y += 6;
        doc.text(
          `Designation: ${formData.designation || "___________________"}`,
          margin,
          y,
        );
        y += 6;
        doc.text(`Acceptance Date: ${dateStr}`, margin, y);
        y += 12;

        // NABL Official (bound from state)
        doc.setFont(font, "bold");
        doc.setFontSize(11);
        doc.text("NABL Official", margin, y);
        y += 8;
        doc.setFont(font, "normal");
        doc.setFontSize(10);
        doc.text(
          `Name of NABL Official: ${nablOfficialName || "___________________"}`,
          margin,
          y,
        );
        y += 6;
        doc.text(`Date of Receipt: ${dateStr}`, margin, y);

        addFooter();

        const pdfBlob = doc.output("blob");
        resolve(pdfBlob);
      } catch (error) {
        reject(error);
      }
    });
  };

  // ─── Submit Handler ──────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/assessor-logistic/agreement`,
        {
          chiefExecutiveName: formData.chiefExecutiveName,
          designation: formData.designation,
          orgId: orgId,
          appId: appId,
        },
      );

      if (!response.data.success) {
        toast.error(response.data.message || "Failed to save agreement");
        setLoading(false);
        return;
      }

      toast.success("Agreement details saved successfully");
      const responseAgreementDO = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor-logistic/agreement/details/${orgId}/${appId}`,
      );
      if (responseAgreementDO.data.success && responseAgreementDO.data.data) {
       const data = responseAgreementDO.data.data;
       const pdfBlob = await generatePDFBlob(data.LoginName || "", data.InsertDate || "");

      const formDataUpload = new FormData();
      const fileName = `Assessor_Logistics_Agreement_${appId}.pdf`;
      formDataUpload.append("file", pdfBlob, fileName);

      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/files/upload`,
        formDataUpload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (!uploadResponse.data.success) {
        throw new Error("Failed to upload file to S3");
      }

      const publicUrl = uploadResponse.data.data.publicUrl;
      const uploadedFileName = uploadResponse.data.data.fileName || fileName;

      const updateResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/assessor-logistic/agreement/pdf`,
        {
          appId: appId,
          orgId: orgId,
          pdfName: uploadedFileName,
          pdfUrl: publicUrl,
        },
      );

      if (!updateResponse.data.success) {
        throw new Error(
          updateResponse.data.message || "Failed to update PDF details",
        );
      }
    }
     setTimeout(() => {
       navigate("/main/dashboard", { replace: true });
      }, 1500);
    } catch (error) {
      console.error("Error in process:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to complete agreement process",
      );
    } finally {
      setLoading(false);
    }
  };

  // ─── Render UI ──────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ py: 2, mt: 8 }}>
      <Paper
        elevation={3}
        sx={{ p: { xs: 2, md: 5 }, border: "1px solid #d9d9d9" }}
      >
        {/* Title */}
        <Typography
          variant="h5"
          align="center"
          fontWeight="bold"
          sx={{
            textDecoration: "underline",
            mb: 3,
            fontFamily: '"Times New Roman", Times, serif',
          }}
        >
          ASSESSOR LOGISTICS AGREEMENT
        </Typography>

        {/* Introduction */}
        <Typography
          variant="body1"
          align="justify"
          sx={{
            mb: 2,
            fontSize: "16px",
            lineHeight: 1.55,
            fontFamily: '"Times New Roman", Times, serif',
          }}
        >
          The terms and conditions mentioned in this document are considered as
          assessor logistics agreement between QCI and Conformity Assessment
          Body (CAB). By accepting these terms and conditions, it is implied
          that a CAB as an applicant and after obtaining accreditation agrees to
          comply at all times with all the Terms and Conditions mentioned in the
          agreement.
        </Typography>

        <Typography
          variant="body1"
          align="justify"
          sx={{
            mb: 2,
            fontSize: "16px",
            fontFamily: '"Times New Roman", Times, serif',
          }}
        >
          QCI and the CAB are hereinafter individually referred to as a "Party"
          and collectively as the "Parties."
        </Typography>

        {/* Section 1 */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, fontSize: "17px" }}
        >
          1. Scope and Nature of Services
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          1.1. The parties acknowledges that QCI acts solely as a facilitator
          and coordinator of logistics for Assessors/Technical Experts deployed
          for the CAB's assessment(s) and does not function as a travel agency,
          transport operator, hotel operator, insurer or guarantor.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          1.2. Nothing contained in this agreement shall be construed as
          creating any contractual relationship between QCI and any Third-Party
          Service Provider on behalf of the CAB.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          1.3. QCI shall exercise reasonable care while facilitating logistics
          arrangements. However, QCI shall not be liable for delays,
          cancellations, overbooking, denial of boarding, baggage loss, force
          majeure events, service deficiencies or any other acts or omissions
          attributable to Third-Party Service Providers.
        </Typography>

        {/* Section 2 */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, fontSize: "17px" }}
        >
          2. Responsibilities of QCI:
        </Typography>
        {[
          "2.1. QCI shall facilitate travel (air/rail/road) and accommodation for Assessors/Technical Experts conducting CAB assessment activities as per eligibility and travel preference as detailed in Annexure I as amended from time to time.",
          "2.2. QCI shall endeavour to arrange travel (flights/train/bus) so that Assessors/Technical Experts arrive in the assessment city a day prior to commencement of the assessment, preferably in the afternoon/evening.",
          "2.3. QCI shall endeavour to arrange return travel on the assessment day, allowing sufficient time to reach the airport after completion of assessment duration. If suitable flights are unavailable or the location is remote, return travel may be arranged for the next day.",
          "2.4. QCI shall book refundable air tickets in Economy Class or, for rail travel, 2nd Class AC tickets or such class as may be prescribed by QCI from time to time.",
          "2.5. QCI shall book the tickets, which will be including meals for the Assessor/Technical Expert.",
          "2.6. QCI shall book the accommodation/hotel for Assessor/Technical Expert as per the eligibility criteria approved by QCI. The currently approved criteria are available at Annexure.",
          "2.7. QCI shall, to the extent possible, make the stay arrangement near to the assessment venue.",
          "2.8. QCI shall make Hotel arrangements including meals (Breakfast & Dinner). In case due to limited flight, the Assessor arrives early, lunch also will be provided and will be charged to the CAB.",
          "2.9. QCI shall invoice to the CAB for the expenses towards Travel/lodging & Boarding on actual basis.",
          "2.10. QCI shall share the tickets/hotel booking to the CAB for coordination.",
        ].map((text, index) => (
          <Typography
            key={index}
            variant="body2"
            align="justify"
            sx={{ mb: 1, ml: 3, fontSize: "16px" }}
          >
            {text}
          </Typography>
        ))}

        {/* Section 3 */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, fontSize: "17px" }}
        >
          3. Responsibilities of the CAB:
        </Typography>
        {[
          "3.1. CAB shall arrange the local travel for Assessor/Technical Expert in the city where assessment is scheduled including to & from Airport pickup/drop & hotel to assessment venue.",
          "3.2. In case where local Assessor/Technical Expert is deputed, the local transportation/taxi for the Assessor/Technical Expert will be arranged by CAB.",
          "3.3. CAB will make the arrangements for working lunch during the Assessment days.",
          "3.4. CAB shall reimburse QCI for invoiced expenses within 30 days from the invoice date. A late fee of 1% per month shall apply for the payments delayed beyond 30 days.",
          "3.5. CAB shall bear the cancellation or modification charges levied by Airlines, hotel or service providers except in the following cases:",
        ].map((text, index) => (
          <Typography
            key={index}
            variant="body2"
            align="justify"
            sx={{ mb: 1, ml: 3, fontSize: "16px" }}
          >
            {text}
          </Typography>
        ))}
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 6, fontSize: "16px" }}
        >
          a. If QCI request changes or cancellations after bookings have been
          confirmed.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 6, fontSize: "16px" }}
        >
          b. If the assessor's schedule is altered due to last-minute requests
          or internal changes by QCI.
        </Typography>
        {[
          "3.6. CAB shall adhere to the timeline for payment and shall not withhold, deduct or set off payments against any claims, dispute or adjustment unless agreed in writing by QCI.",
          "3.7. CAB shall not attempt to influence, directly or indirectly, the Assessor(s) in discharge of their professional duties.",
          "3.8. All arrangements made by CAB shall be reasonable, non-influencing and compliant with QCI policies.",
          "3.9. Any attempt to provide inducement, hospitality beyond norms, or influence the assessment process shall constitute serious non-compliance and material breach to this agreement.",
        ].map((text, index) => (
          <Typography
            key={`3_${index + 6}`}
            variant="body2"
            align="justify"
            sx={{ mb: 1, ml: 3, fontSize: "16px" }}
          >
            {text}
          </Typography>
        ))}

        {/* Section 4 */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, fontSize: "17px" }}
        >
          4. Data Protection and Privacy (DPDP Act 2023)
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          4.1. The Parties agree that any personal data processed under this
          Agreement shall be limited strictly to what is necessary for
          facilitating assessor logistics and related assessment activities.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          4.2. The CAB shall process such data strictly in accordance with the
          scope communicated by QCI ensuring the compliance of DPDP Act and
          Rules.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          4.3. The CAB shall implement reasonable security safeguards to prevent
          unauthorized access, disclosure, alteration, or loss of personal data,
          in compliance with the Digital Personal Data Protection Act, 2023. CAB
          shall promptly notify the QCI upon becoming aware of any personal data
          breach affecting information processed under this Agreement and shall
          reasonably cooperate in mitigating its consequences.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          4.4. QCI shall implement reasonable security safeguards in compliance
          with Digital Personal Data Protection Act, 2023, and applicable rules,
          strictly for the specified purpose.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          4.5. The CAB represents/warrants/confirms that it has the lawful
          authority to share any personal data provided to QCI for the purposes
          of this Agreement.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          4.6. Personal data may be shared only with third-party service
          providers such as airlines, hotels, and transport agencies, strictly
          to the extent required for fulfilment of the specified purpose.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          4.7. Personal data shall not be retained beyond the period necessary
          for completion of the assessment and settlement of related
          obligations, unless required by law.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          4.8. Both Parties shall maintain confidentiality of information
          exchanged under this Agreement, except where disclosure is required
          under law or regulatory directions.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          4.9. Nothing in this Agreement shall be construed as creating
          obligations exceeding those mandated under applicable data protection
          law.
        </Typography>

        {/* Section 5 */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, fontSize: "17px" }}
        >
          5. Confidentiality
        </Typography>
        <Typography
          variant="body1"
          align="justify"
          sx={{ mb: 2, fontSize: "16px" }}
        >
          Both Parties shall treat all information exchanged under this
          Agreement as confidential and not disclose it to third parties without
          prior written consent, except as required by law.
        </Typography>
        <Typography
          variant="body1"
          align="justify"
          sx={{ mb: 2, fontSize: "16px" }}
        >
          CAB shall keep confidential all information received from QCI or its
          personnel in the course of the assessment and shall not disclose it to
          any third party except without prior written consent or as required by
          law.
        </Typography>

        {/* Section 6 */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, fontSize: "17px" }}
        >
          6. Indemnity
        </Typography>
        <Typography
          variant="body1"
          align="justify"
          sx={{ mb: 2, fontSize: "16px" }}
        >
          The CAB shall indemnify, defend and hold harmless QCI, its Boards,
          employees, Assessors and Technical Experts from and against all
          claims, losses, damages, liabilities, costs and expenses including
          legal fees arising out of or in connection with (a) acts or omissions
          of the CAB or its personnel; (b) breach of this Agreement by the CAB;
          or (c) injury, death, loss or damage sustained by any Personnel while
          performing Assessment Activities due to conditions attributable to the
          CAB. The CAB shall indemnify QCI against claims arising from logistics
          mismanagement or DPDP non-compliance attributable to the CAB.
        </Typography>

        {/* Section 7 */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, fontSize: "17px" }}
        >
          7. Limitation of Liability
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          7.1. Except for liability arising from fraud, wilful misconduct or
          obligations expressly assumed under this Agreement, QCI shall not be
          liable for any indirect, consequential, incidental, special or
          exemplary damages, including loss of business, revenue, profits,
          goodwill or opportunity.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          7.2. QCI shall not be liable for any delay, cancellation, rescheduling
          or service deficiency attributable to Third-Party Service Providers or
          events beyond its reasonable control.
        </Typography>

        {/* Section 8 */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, fontSize: "17px" }}
        >
          8. Force Majeure:
        </Typography>
        <Typography
          variant="body1"
          align="justify"
          sx={{ mb: 2, fontSize: "16px" }}
        >
          Neither party shall be liable for service deficiencies, delays, or
          disruptions caused by airlines, hotels, railways, or other travel
          vendors, including those due to force majeure events such as natural
          disasters, strikes, epidemics, pandemics, war, civil unrest, strikes,
          governmental restrictions, failure of transportation systems or other
          unforeseen circumstances beyond parties reasonable control.
        </Typography>

        {/* Section 9 */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, fontSize: "17px" }}
        >
          9. Term and Validity:
        </Typography>
        <Typography
          variant="body1"
          align="justify"
          sx={{ mb: 2, fontSize: "16px" }}
        >
          Unless either Party gives the other Party at least 30 days' prior
          notice of its intention not to renew, this Agreement shall continue on
          assessment basis, for every new planned assessment, on the same terms
          and conditions.
        </Typography>
        <Typography
          variant="body1"
          align="justify"
          sx={{ mb: 2, fontSize: "16px" }}
        >
          Notwithstanding the expiry of the Term, where any assessment activity
          or travel/hotel bookings have already been scheduled or committed by
          QCI before expiry, the provisions of this Agreement including payment
          obligations shall continue to apply until completion of such activity
          and settlement of all dues.
        </Typography>

        {/* Section 10 */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, fontSize: "17px" }}
        >
          10. Termination:
        </Typography>
        <Typography
          variant="body1"
          align="justify"
          sx={{ mb: 2, fontSize: "16px" }}
        >
          Either Party may terminate this Agreement by giving 30 (thirty) days'
          prior written notice to the other Party. Termination shall not affect
          rights and obligations accrued prior to termination. In the event of
          such termination:
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 6, fontSize: "16px" }}
        >
          (a) QCI shall be entitled to receive full payment for:
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 9, fontSize: "16px" }}
        >
          (i) all amounts invoiced and unpaid as of the termination date; and
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 9, fontSize: "16px" }}
        >
          (ii) all non-cancellable or committed expenses incurred on behalf of
          the CAB prior to termination.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 6, fontSize: "16px" }}
        >
          (b) Any bookings already made for Assessors/Technical Experts may
          either be utilized by the CAB or cancelled at the CAB's cost including
          cancellation/modification charges.
        </Typography>
        <Typography
          variant="body1"
          align="justify"
          sx={{ mb: 2, fontSize: "16px" }}
        >
          QCI may terminate this Agreement immediately by written notice if the
          QCI:
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 6, fontSize: "16px" }}
        >
          (i) fails to make timely payments,
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 6, fontSize: "16px" }}
        >
          (ii) breaches any material provision of this Agreement, or
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 6, fontSize: "16px" }}
        >
          (iii) engages in conduct that, in QCI's reasonable opinion, adversely
          affects QCI's reputation or the integrity of the assessment process.
        </Typography>
        <Typography
          variant="body1"
          align="justify"
          sx={{ mb: 2, fontSize: "16px" }}
        >
          Upon such termination, all amounts due to QCI shall become immediately
          payable.
        </Typography>

        {/* Section 11 */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, fontSize: "17px" }}
        >
          11. Dispute Resolution:
        </Typography>
        <Typography
          variant="body1"
          align="justify"
          sx={{ mb: 2, fontSize: "16px" }}
        >
          In the event of any dispute arising out of or in connection with this
          Agreement, the parties shall try to resolve the dispute amicably by
          mutual discussion. The unresolved disputes shall be referred to the
          Sole Arbitrator to be appointed by the mutual consent of the parties
          and the arbitration proceedings shall be conducted in accordance with
          the Arbitration and Conciliation Act, 1996 as amended upto date. In
          the event the Parties are unable to reach mutual consensus on the
          appointment of the Sole Arbitrator within thirty (30) days from the
          date of invocation of arbitration, the Sole Arbitrator shall be
          appointed in accordance with the provisions of Section 11 of the
          Arbitration and Conciliation Act, 1996, by the competent court. The
          venue/seat of the arbitration proceedings shall be at New Delhi. The
          arbitration proceedings shall be conducted in English language. The
          decision rendered by the Sole Arbitrator shall be final and binding on
          both Parties. Nothing contained herein shall prevent either Party from
          approaching the courts at New Delhi for interim reliefs, including
          injunctions, pending completion of the arbitration proceedings.
        </Typography>

        {/* Section 12 */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, fontSize: "17px" }}
        >
          12. Governing Law and Territorial Jurisdiction:
        </Typography>
        <Typography
          variant="body1"
          align="justify"
          sx={{ mb: 2, fontSize: "16px" }}
        >
          This Agreement shall be governed by and construed in accordance with
          the laws of India. The parties hereby agree that the courts at New
          Delhi shall have exclusive territorial jurisdiction over any dispute
          arising out of or in connection with this Agreement.
        </Typography>

        {/* Section 13 */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, fontSize: "17px" }}
        >
          13. General Provisions
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          13.1. Nothing contained herein shall create any partnership, agency,
          employment or joint venture between the Parties.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          13.2. Any amendment shall be valid only if made in writing and signed
          by both Parties.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          13.3. If any provision is held invalid or unenforceable, the remaining
          provisions shall continue in full force and effect.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          13.4. Failure by either Party to enforce any provision shall not
          constitute a waiver of its rights.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          13.5. Neither Party shall assign its rights or obligations under this
          Agreement without the prior written consent of the other Party.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          13.6. Notices under this Agreement shall be in writing and delivered
          by registered post, courier or electronic mail to the addresses
          notified by the Parties.
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, ml: 3, fontSize: "16px" }}
        >
          13.7. The provisions relating to payment obligations, confidentiality,
          indemnity, limitation of liability, data protection, dispute
          resolution and any other provision intended by its nature to survive
          shall continue notwithstanding expiry or termination of this
          Agreement.
        </Typography>

        {/* Section 14 */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, fontSize: "17px" }}
        >
          14. Entire Agreement & Amendments
        </Typography>
        <Typography
          variant="body2"
          align="justify"
          sx={{ mb: 1, fontSize: "16px" }}
        >
          This Agreement constitutes the entire understanding between the
          Parties. Any amendments shall be in writing and signed by authorized
          representatives of the Parties.
        </Typography>

        <Typography
          variant="body1"
          fontWeight="bold"
          align="justify"
          sx={{ mt: 2, mb: 2, fontSize: "16px" }}
        >
          IN WITNESS WHEREOF, the Parties hereto have executed this Agreement as
          of the day and year written herein above.
        </Typography>

        <Box sx={{ borderTop: "1px dashed #444", my: 3 }} />

        {/* Annexure */}
        <Typography
          variant="h6"
          align="center"
          fontWeight="bold"
          sx={{ textDecoration: "underline", mb: 2, fontSize: "20px" }}
        >
          ANNEXURE 1
        </Typography>

        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
          Air Travel Criteria
        </Typography>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1 }}>
          Domestic Travel
        </Typography>
        <ul style={{ marginLeft: "30px" }}>
          <li>
            Air tickets shall be booked based on the best available rates at the
            time of booking.
          </li>
          <li>Class & Seat Preference: Economy Class only.</li>
          <li>
            Seat Preference: Seat will be booked in the order: Aisle &gt;
            Window.
          </li>
          <li>Meal Entitlement: Standard airline meal included in fare.</li>
        </ul>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>
          Train:
        </Typography>
        <ul style={{ marginLeft: "30px" }}>
          <li>2nd AC</li>
        </ul>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
          Hotel Eligibility Criteria
        </Typography>
        <Box
          sx={{ border: "1px solid #333", borderRadius: 1, overflow: "hidden" }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f2f2f2" }}>
                <th
                  style={{
                    border: "1px solid #333",
                    padding: "9px",
                    textAlign: "center",
                  }}
                >
                  City Category
                </th>
                <th
                  style={{
                    border: "1px solid #333",
                    padding: "9px",
                    textAlign: "center",
                  }}
                >
                  Cities
                </th>
                <th
                  style={{
                    border: "1px solid #333",
                    padding: "9px",
                    textAlign: "center",
                  }}
                >
                  Hotel Rate (per night)
                  <br />
                  (Single Occupancy Rooms)
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: "1px solid #333", padding: "9px" }}>
                  A-Class Cities
                </td>
                <td style={{ border: "1px solid #333", padding: "9px" }}>
                  Mumbai, Delhi, Kolkata, Chennai, Bengaluru, Hyderabad
                </td>
                <td style={{ border: "1px solid #333", padding: "9px" }}>
                  ₹10,000 + Taxes as applicable
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #333", padding: "9px" }}>
                  B-Class Cities
                </td>
                <td style={{ border: "1px solid #333", padding: "9px" }}>
                  State Capitals and Other Identified Towns (per QCI's need)
                </td>
                <td style={{ border: "1px solid #333", padding: "9px" }}>
                  ₹8,000 + Taxes as applicable
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #333", padding: "9px" }}>
                  C-Class Cities
                </td>
                <td style={{ border: "1px solid #333", padding: "9px" }}>
                  All other cities
                </td>
                <td style={{ border: "1px solid #333", padding: "9px" }}>
                  ₹6,000 + Taxes as applicable
                </td>
              </tr>
            </tbody>
          </table>
        </Box>

        <Typography variant="body2" sx={{ mt: 2, mb: 3, fontSize: "14px" }}>
          Note: For QCI/NABL officials acting as Assessors/Technical Experts,
          travel and accommodation arrangements shall be made as per their
          eligibility under QCI Service Rules.
        </Typography>

        <Box sx={{ borderTop: "1px dashed #444", my: 3 }} />

        {/* Form Section */}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
              Name of Head of the Laboratory *
            </Typography>
            <TextField
              fullWidth
              name="chiefExecutiveName"
              value={formData.chiefExecutiveName}
              onChange={handleChange}
              error={!!errors.chiefExecutiveName}
              helperText={errors.chiefExecutiveName}
              variant="outlined"
              size="small"
              sx={{
                backgroundColor: "#fffdf2",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                  "& fieldset": {
                    borderTop: "none",
                    borderLeft: "none",
                    borderRight: "none",
                    borderBottom: "1px solid #222222",
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
              Designation *
            </Typography>
            <TextField
              fullWidth
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              error={!!errors.designation}
              helperText={errors.designation}
              variant="outlined"
              size="small"
              sx={{
                backgroundColor: "#fffdf2",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                  "& fieldset": {
                    borderTop: "none",
                    borderLeft: "none",
                    borderRight: "none",
                    borderBottom: "1px solid #222222",
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                px: 3,
                py: 1,
                fontSize: "15px",
                backgroundColor: "#00024c",
                "&:hover": {
                  backgroundColor: "#000138",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Accept"
              )}
            </Button>
          </Box>
        </Box>

        <style>
          {`
            @media print {
              .MuiButton-root {
                display: none !important;
              }
            }
          `}
        </style>
      </Paper>
    </Container>
  );
};

export default AssessorLogisticsAgreement;
